"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useOffsetBox = void 0;
var _react = require("react");
let offsetBoxArray = [];
const useOffsetBox = function () {
  let isOpenOnFocus = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  let onClose = arguments.length > 1 ? arguments[1] : undefined;
  let onBlur = arguments.length > 2 ? arguments[2] : undefined;
  let isSelectOnBlur = arguments.length > 3 ? arguments[3] : undefined;
  const isMountedRef = (0, _react.useRef)(false);
  const dropContentRef = (0, _react.useRef)(null);
  const anchorRef = (0, _react.useRef)(null);
  const [isOpen, setIsOpen] = (0, _react.useState)(false);
  const close = (0, _react.useCallback)(() => {
    setIsOpen(false);
  }, []);
  const open = (0, _react.useCallback)(() => {
    setIsOpen(true);
  }, []);
  const handleClose = (0, _react.useCallback)(() => {
    if (onClose) {
      onClose();
    }
    anchorRef.current && anchorRef.current.focus();
    close();
  }, [close, onClose]);
  const toggle = (0, _react.useCallback)(() => {
    if (isOpen) {
      handleClose();
    } else {
      open();
    }
  }, [handleClose, isOpen, open]);
  const isOffsetBoxEvent = (0, _react.useCallback)((event, anchorElement, dropContentElement) => {
    if (event) {
      // Check if the event was inside the anchor button
      if (anchorElement === event.target || anchorElement?.contains(event.target)) return true;

      // Check if the event was inside the drop content by checking the event parents (path)
      let eventParentIndex = 0;
      let path = event.composedPath ? event.composedPath() : event.path;
      path = path?.length ? path : event.target ? [event.target] : [];
      do {
        let eventParent = path?.[eventParentIndex];
        if (eventParent !== window && dropContentElement?.contains(eventParent)) return true;
        eventParentIndex++;
      } while (eventParentIndex < path?.length);
    }
    return false;
  }, []);
  const getDropElementChildren = (0, _react.useCallback)(parentDropElement => {
    const children = [];
    offsetBoxArray.forEach(ob => {
      if (ob.anchorRef && parentDropElement?.contains(ob.anchorRef.current)) {
        children.push(ob);
      }
    });
    return children;
  }, []);
  const handleOffsetBoxBlur = (0, _react.useCallback)(event => {
    if (!dropContentRef?.current) return;
    if (event.key === 'Tab') {
      if (isSelectOnBlur && onClose) {
        onClose();
      }
      handleClose();
    }
    if (isOffsetBoxEvent(event, anchorRef.current, dropContentRef.current)) return;
    const offsetBoxChildren = getDropElementChildren(dropContentRef.current);
    for (let i = 0; i < offsetBoxChildren.length; i++) {
      if (isOffsetBoxEvent(event, offsetBoxChildren[i].anchorRef.current, offsetBoxChildren[i].dropContentRef.current)) return;
    }
    if (isMountedRef.current) {
      if (onBlur) {
        onBlur(event);
      }
      handleClose();
    }
  }, [anchorRef, handleClose, isOffsetBoxEvent, getDropElementChildren, onBlur, isSelectOnBlur, onClose]);
  const handleAnchorFocus = event => {
    if (isOpenOnFocus) {
      setTimeout(() => {
        if (isMountedRef.current) {
          open();
        }
      }, 300);
    }
  };
  const handleAnchorKeyUp = event => {
    switch (event.key) {
      case 'Enter':
      case 'ArrowDown':
        open();
        setTimeout(() => {
          let focusOn = dropContentRef?.current?.querySelector('input') || dropContentRef?.current?.querySelector('[tabindex="0"]');
          focusOn?.focus();
        }, 300);
        break;
      case 'Escape':
        close();
        break;
      default:
        break;
    }
  };
  const handleOffsetBoxLeave = event => {
    if (event.relatedTarget.nodeName !== undefined && (anchorRef.current === event.relatedTarget || anchorRef.current?.contains(event.relatedTarget) || dropContentRef.current === event.relatedTarget || dropContentRef.current?.contains(event.relatedTarget))) {
      return;
    }
    if (isMountedRef.current) {
      close();
    }
  };
  (0, _react.useEffect)(() => {
    isMountedRef.current = true;
    const index = offsetBoxArray.length;
    offsetBoxArray.push({
      id: index,
      anchorRef,
      dropContentRef
    });
    return () => {
      offsetBoxArray = offsetBoxArray.filter(_ref => {
        let {
          id
        } = _ref;
        return id !== index;
      });
      isMountedRef.current = false;
    };
  }, []);
  (0, _react.useEffect)(() => {
    if (isOpen) {
      window.addEventListener('resize', close);
      window.addEventListener('scroll', close);
      document.querySelector('#rootScroll')?.addEventListener('scroll', close);
    }
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close);
      document.querySelector('#rootScroll')?.removeEventListener('scroll', close);
    };
  }, [close, isOpen]);
  (0, _react.useEffect)(() => {
    document.addEventListener('click', handleOffsetBoxBlur);
    return () => {
      document.removeEventListener('click', handleOffsetBoxBlur);
    };
  }, [handleOffsetBoxBlur]);
  (0, _react.useEffect)(() => {
    const listener = event => {
      if (event.key === 'Tab' && event.shiftKey && event.target.getAttribute('data-value') === '1') {
        // Shift + Tab on first element will return focus to button
        anchorRef.current && anchorRef.current.focus();
      }
      setTimeout(() => {
        if (event.key === 'Escape') {
          close();
        } else if (event.key === 'Tab') {
          handleOffsetBoxBlur(event);
        }
      }, 0);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
      document.removeEventListener('keydown', listener);
    };
  }, [close, handleOffsetBoxBlur]);
  return {
    dropContentRef,
    anchorRef,
    handleAnchorFocus,
    handleAnchorKeyUp,
    handleOffsetBoxLeave,
    handleOffsetBoxBlur,
    toggle,
    close,
    open,
    isOpen
  };
};
exports.useOffsetBox = useOffsetBox;